/**
 * The screen under a full-screen layer, and how far it is covered (`D193`).
 *
 * Material's shared axis moves BOTH surfaces: the layer coming in slides in
 * from the right while the screen it covers slides a little left, and Back
 * plays it reversed. The screen is not the layer's to animate — it belongs to
 * the shell — so the two meet here, in one motion value outside React:
 * `coverage`, 0 when no full-screen layer covers the screen, 1 when one does.
 *
 * - A layer calls `useCoverUnderlay(active)` while it is present; the value
 *   animates to 1 on its way in and back to 0 when its exit starts.
 * - The shell reads it into the screen's transform (`features/shell`), so
 *   nothing re-renders per frame.
 * - The predictive Back writes it directly while a gesture is in progress
 *   (`app/predictive-back.ts`): the layer shrinks away and the screen under
 *   it slides home with the finger.
 */
import {
  type AnimationPlaybackControls,
  animate,
  motionValue,
} from "motion/react";
import { useEffect } from "react";

/** How far left the covered screen sits, in percent of its width. */
export const UNDERLAY_SHIFT_PERCENT = 6;

/** Material 3 durations and curves, shared by every full-screen layer. */
export const ENTER_MS = 300;
export const EXIT_MS = 200;
export const EMPHASIZED_DECELERATE = [0.05, 0.7, 0.1, 1] as const;
export const EMPHASIZED_ACCELERATE = [0.3, 0, 0.8, 0.15] as const;

export const coverage = motionValue(0);

const coverers = new Set<symbol>();
let held = false;
/**
 * The ONE animation writing `coverage`, kept here so whatever writes next
 * stops it first. `coverage.stop()` alone did not: a settle still running
 * when a Back gesture started kept writing over the gesture's own values.
 */
let running: AnimationPlaybackControls | null = null;

function stopCoverage(): void {
  running?.stop();
  running = null;
}

/** Sets `coverage` by hand, stopping anything animating it. */
export function setCoverage(value: number): void {
  stopCoverage();
  coverage.set(value);
}

/** Animates `coverage` to `target`, replacing anything animating it. */
export function animateCoverage(target: number, entering: boolean): void {
  stopCoverage();
  running = animate(coverage, target, {
    duration: (entering ? ENTER_MS : EXIT_MS) / 1000,
    ease: entering ? [...EMPHASIZED_DECELERATE] : [...EMPHASIZED_ACCELERATE],
  });
}

function settle(): void {
  if (held) {
    return;
  }
  const covered = coverers.size > 0;
  animateCoverage(covered ? 1 : 0, covered);
}

/**
 * While a gesture drives `coverage` by hand, the layers' own enter and exit
 * leave it alone; `release` hands it back and animates to wherever the
 * covering layers now say it should be.
 */
export function holdCoverage(): () => void {
  held = true;
  stopCoverage();
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    held = false;
    settle();
  };
}

/** Whether any full-screen layer covers the screen right now. */
export function isCovered(): boolean {
  return coverers.size > 0;
}

/**
 * Covers the screen while `active`. Reduced motion and the shot harness get
 * no slide: the shell ignores `coverage` there (`features/shell/layout.tsx`).
 */
export function useCoverUnderlay(active: boolean): void {
  useEffect(() => {
    if (!active) {
      return;
    }
    const id = Symbol("coverer");
    coverers.add(id);
    settle();
    return () => {
      coverers.delete(id);
      settle();
    };
  }, [active]);
}
